import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Alert, Tabs, Switch, message, Button, Modal } from "antd";
import {
  ShoppingCartOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import { Send, Wallet, ArrowRightLeft } from "lucide-react";

// Components
import BikerDashboardHeader from "./components/BikerDashboardHeader";
import BikerControls from "./components/BikerControls";
import TodayDeliveries from "./components/TodayDeliveries";
import PreviousDeliveries from "./components/PreviousDeliveries";
import DeliveryDetailsModal from "./components/DeliveryDetailsModal";
import ClusterAcceptanceModal from "../../components/delivery/ClusterAcceptanceModal";
import DeliveryLayout from "./DeliveryLayout";
import PendingOrdersList from "./components/PendingOrdersList";
import AcceptedOrdersList from "./components/AcceptedOrdersList";
import CompletedOrdersList from "./components/CompletedOrdersList";
import { TransactionOutlined } from "@ant-design/icons";

// Custom hooks
import useDeliveryData from "./hooks/useDeliveryData";
import useClusterAllocation from "../../hooks/useClusterAllocation";
import useDeliverySocket from "../../hooks/useDeliverySocket";
import { _update_biker_availability } from "../../common/axios_services";
import HelpSupportButton from "../../components/HelpSupportButton";
import DriverPendingModal from "../../components/modals/DriverPendingModal";
import PendingActivitiesBanner from "../../components/PendingActivitiesBanner";

const DeliveryHome = () => {
  const navigate = useNavigate();
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderModalVisible, setOrderModalVisible] = useState(false);
  const [driverPendingModalVisible, setDriverPendingModalVisible] = useState(false);
  const [activeDeliveryTab, setActiveDeliveryTab] = useState("pending");
  const [isAvailable, setIsAvailable] = useState(() => {
    const saved = localStorage.getItem("biker_availability");
    return saved ? JSON.parse(saved) : false;
  });
  const [clusterModalVisible, setClusterModalVisible] = useState(false);
  const [statusModalVisible, setStatusModalVisible] = useState(false);

  const {
    metrics,
    bikerProfile,
    loading,
    error,
    refreshData,
    pickupOrder,
    deliverOrder,
    availableOrders,
    acceptedOrders,
    completedOrders,
    availablePagination,
    acceptedPagination,
    completedPagination,
    updateAvailableFilters,
    updateAcceptedFilters,
    updateCompletedFilters,
    acceptOrderAction,
    reportBuyerAbsentAction,
    startDeliveryAction,
    arrivedDeliveryAction,
    transactions,
    transactionPagination,
    updateTransactionFilters,
    getTransactionHistory,
  } = useDeliveryData();

  const hasAutoOpenedDriverRef = useRef(false);
  const activeDriverActivitiesCount = useMemo(() => {
    return (Array.isArray(metrics?.activeDeliveries)
      ? metrics.activeDeliveries
      : Array.isArray(availableOrders)
        ? availableOrders
        : []
    ).filter((d) =>
      ["WAITING", "ACCEPTED", "READY", "IN_TRANSIT", "pending", "ready", "in_transit"].includes(
        d.deliveryStatus || d.status || d.delivery_status
      )
    ).length;
  }, [metrics, availableOrders]);

  useEffect(() => {
    if (!hasAutoOpenedDriverRef.current && activeDriverActivitiesCount > 0) {
      setDriverPendingModalVisible(true);
      hasAutoOpenedDriverRef.current = true;
    }
  }, [activeDriverActivitiesCount]);

  const bikerVehicleType =
    localStorage.getItem("biker_delivery_mode") || "BIKE";

  const {
    filteredClusters,
    selectedCluster,
    accepting,
    isEligible,
    selectCluster,
    acceptCluster,
    fetchClusters,
  } = useClusterAllocation(bikerVehicleType);

  useDeliverySocket(null, null, null, null);

  useEffect(() => {
    if (
      bikerProfile?.bikers?.status &&
      bikerProfile.bikers.status !== "approved"
    ) {
      setStatusModalVisible(true);
    }
  }, [bikerProfile]);

  const handleViewOrder = (order) => {
    setSelectedOrder(order);
    setOrderModalVisible(true);
  };

  const handleCloseModal = () => {
    setOrderModalVisible(false);
    setSelectedOrder(null);
  };

  const handleClusterModalOpen = () => {
    if (filteredClusters.length > 0) {
      selectCluster(filteredClusters[0]);
      setClusterModalVisible(true);
    } else {
      message.info("No available clusters for your vehicle type");
    }
  };

  const handleClusterModalClose = () => {
    setClusterModalVisible(false);
  };

  const handleAcceptCluster = async (clusterId) => {
    try {
      await acceptCluster(clusterId);
      setClusterModalVisible(false);
      refreshData();
    } catch (error) {
      console.error("Error accepting cluster:", error);
    }
  };

  const handleAvailabilityToggle = async (checked) => {
    try {
      await _update_biker_availability(checked);
      setIsAvailable(checked);
      localStorage.setItem("biker_availability", JSON.stringify(checked));
      message.success(
        checked
          ? "You are now available for deliveries"
          : "You are now unavailable for deliveries",
      );
    } catch (error) {
      message.error(
        error?.response?.data?.message ||
        error?.message ||
        "Error updating availability",
      );
    }
  };

  const getStatusModalConfig = (status) => {
    switch (status) {
      case "pending":
        return {
          title: "Account Under Review",
          icon: (
            <ClockCircleOutlined
              style={{ fontSize: "48px", color: "#faad14" }}
            />
          ),
          message:
            "Your account is currently under review. We'll notify you once the approval process is complete.",
          bgColor: "bg-yellow-50",
          borderColor: "border-yellow-200",
          textColor: "text-yellow-800",
        };
      case "rejected":
        return {
          title: "Account Rejected",
          icon: (
            <CloseCircleOutlined
              style={{ fontSize: "48px", color: "#ff4d4f" }}
            />
          ),
          message:
            "Unfortunately, your account application was not approved. Please contact support for more information.",
          bgColor: "bg-red-50",
          borderColor: "border-red-200",
          textColor: "text-red-800",
        };
      case "suspended":
        return {
          title: "Account Suspended",
          icon: (
            <ExclamationCircleOutlined
              style={{ fontSize: "48px", color: "#ff4d4f" }}
            />
          ),
          message:
            "Your account is currently suspended. Please contact support for assistance.",
          bgColor: "bg-red-50",
          borderColor: "border-red-200",
          textColor: "text-red-800",
        };
      default:
        return null;
    }
  };

  const handleContinue = () => {
    navigate("/home");
  };

  if (error) {
    return (
      <Alert
        message="Error Loading Delivery Portal"
        description={error}
        type="error"
        showIcon
      />
    );
  }

  const statusConfig = getStatusModalConfig(bikerProfile?.bikers?.status);
  const isRestaurantBiker =
    bikerProfile?.bikers?.bikerType?.toLowerCase() == "restaurant";

  return (
    <>
      {statusModalVisible && statusConfig && (
        <Modal
          open={statusModalVisible}
          footer={null}
          closable={false}
          maskClosable={false}
          centered
          width={500}
        >
          <div
            className={`p-8 rounded-lg ${statusConfig.bgColor} ${statusConfig.borderColor} border-2 text-center`}
          >
            <div className="mb-6">{statusConfig.icon}</div>
            <h2 className={`text-2xl font-bold mb-4 ${statusConfig.textColor}`}>
              {statusConfig.title}
            </h2>
            <p className={`text-lg ${statusConfig.textColor} leading-relaxed`}>
              {statusConfig.message}
            </p>
            <div className="mt-6">
              <Button
                onClick={handleContinue}
                className="bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition"
              >
                Continue
              </Button>
            </div>
          </div>
        </Modal>
      )}
      <DeliveryLayout>
        <div className="pb-40">
          <BikerDashboardHeader metrics={metrics} loading={loading} />

          <PendingActivitiesBanner
            title="Driver Activities"
            subtitle="Track pending acceptance, pickup codes & in-transit deliveries"
            buttonText="View Activities"
            count={
              (Array.isArray(metrics?.activeDeliveries)
                ? metrics.activeDeliveries
                : Array.isArray(availableOrders)
                  ? availableOrders
                  : []
              ).filter((d) =>
                ["WAITING", "ACCEPTED", "READY", "IN_TRANSIT", "pending", "ready", "in_transit"].includes(
                  d.deliveryStatus || d.status || d.delivery_status
                )
              ).length
            }
            onClick={() => setDriverPendingModalVisible(true)}
          />

          <BikerControls
            isAvailable={isAvailable}
            handleAvailabilityToggle={handleAvailabilityToggle}
            onTransferComplete={refreshData}
            loading={loading}
            walletBalance={metrics?.walletBalance}
          />

          {metrics.pendingOrders > 0 && (
            <Alert
              message={`${metrics.pendingOrders} deliveries pending`}
              description="You have orders waiting to be picked up."
              type="warning"
              showIcon
              style={{ marginBottom: "20px" }}
            />
          )}

          {isEligible && (
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-blue-900">
                    Available Clusters
                  </h3>
                  <p className="text-sm text-blue-700">
                    {filteredClusters.length} next-day delivery cluster(s)
                    available
                  </p>
                </div>
                <Button
                  type="primary"
                  icon={<ShoppingCartOutlined />}
                  onClick={handleClusterModalOpen}
                  disabled={filteredClusters.length === 0}
                >
                  View Clusters
                </Button>
              </div>
            </div>
          )}

          <Tabs
            activeKey={activeDeliveryTab}
            onChange={(key) => {
              setActiveDeliveryTab(key);
              refreshData();
            }}
            type="card"
            className="delivery-tabs"
          >
            {!isRestaurantBiker && (
              <Tabs.TabPane tab="Pending Delivery" key="pending">
                <div className="mt-6">
                  <PendingOrdersList
                    orders={availableOrders}
                    loading={loading}
                    pagination={availablePagination}
                    onAccept={acceptOrderAction}
                    updateFilters={updateAvailableFilters}
                  />
                </div>
              </Tabs.TabPane>
            )}

            <Tabs.TabPane tab="Accepted Delivery" key="accepted">
              <div className="mt-6">
                <AcceptedOrdersList
                  orders={acceptedOrders}
                  loading={loading}
                  pagination={acceptedPagination}
                  onViewOrder={handleViewOrder}
                  updateFilters={updateAcceptedFilters}
                />
              </div>
            </Tabs.TabPane>

            <Tabs.TabPane tab="Completed Delivery" key="completed">
              <div className="mt-6">
                <CompletedOrdersList
                  orders={completedOrders}
                  loading={loading}
                  pagination={completedPagination}
                  onViewOrder={handleViewOrder}
                  updateFilters={updateCompletedFilters}
                />
              </div>
            </Tabs.TabPane>
          </Tabs>

          <DeliveryDetailsModal
            visible={orderModalVisible}
            order={selectedOrder}
            onCancel={handleCloseModal}
            onPickupOrder={pickupOrder}
            onDeliverOrder={deliverOrder}
            onReportBuyerAbsent={reportBuyerAbsentAction}
            loading={loading}
            onStartDelivery={startDeliveryAction}
            onArrived={arrivedDeliveryAction}
          />

          <ClusterAcceptanceModal
            visible={clusterModalVisible}
            cluster={selectedCluster}
            onAccept={handleAcceptCluster}
            onCancel={handleClusterModalClose}
            loading={accepting}
            bikerVehicleType={bikerVehicleType}
          />

          <DriverPendingModal
            visible={driverPendingModalVisible}
            onClose={() => setDriverPendingModalVisible(false)}
            activeDeliveries={metrics?.activeDeliveries || availableOrders || []}
            onRefresh={refreshData}
            onViewOrder={(item) => {
              setDriverPendingModalVisible(false);
              setSelectedOrder(item);
              setOrderModalVisible(true);
            }}
          />
        </div>
      </DeliveryLayout>
      <HelpSupportButton page="DRIVER_HOME" />
    </>
  );
};

export default DeliveryHome;
